import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Backdrop,
  Fade,
  LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface TourStep {
  id: string;
  target: string; // CSS selector
  title: string;
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface GuidedTourProps {
  run: boolean;
  onTourEnd: () => void;
  onStepChange?: (stepIndex: number, stepId: string) => void;
  userRole?: 'kontor' | 'byra' | 'admin';
}

// Definiera alla tour-steg
const createSteps = (role: string = 'kontor'): TourStep[] => {
  const baseSteps: TourStep[] = [
    {
      id: 'intro',
      target: 'body',
      title: 'Låt oss börja! 🎯',
      content: (
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          Jag visar dig de viktigaste funktionerna i DAM-systemet. 
          Du kan när som helst hoppa över eller gå tillbaka.
        </Typography>
      ),
      placement: 'center'
    },
    {
      id: 'search',
      target: '.advanced-search-bar',
      title: '🔍 Sökfältet',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Här söker du efter bilder. Skriv produktnamn, artikelnummer eller beskrivning.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            <strong>Tips:</strong> Sök på "feta 500g" för att hitta specifika förpackningar.
          </Typography>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      id: 'categories',
      target: '.category-filter',
      title: '📂 Kategorier',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Filtrera på kategori för att snabbt hitta rätt typ av material.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Du kan välja flera kategorier samtidigt!
          </Typography>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      id: 'asset-grid',
      target: '.asset-grid',
      title: '🖼️ Bildbiblioteket',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Här visas alla bilder som matchar din sökning. 
            Klicka på en bild för att se detaljer och ladda ner.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            <strong>Tips:</strong> Använd slidern längst upp för att ändra storlek på miniatyrerna.
          </Typography>
        </Box>
      ),
      placement: 'center'
    },
    {
      id: 'collections',
      target: '.collection-sidebar',
      title: '📁 Collections',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Organisera bilder i collections – perfekt för kampanjer eller projekt!
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Dra och släpp bilder hit för att lägga till dem i en collection.
          </Typography>
        </Box>
      ),
      placement: 'right'
    },
    {
      id: 'cart',
      target: '.cart-button',
      title: '🛒 Kundvagn',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Beställ butiksmaterial genom att gå in på kategorin "butiksmaterial".
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Klicka på varukorgs-ikonen för att se vilket butiksmaterial du lagt i korgen.
          </Typography>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      id: 'brand-guide',
      target: '.brand-guide-button',
      title: '📖 Grafisk Manual',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Här finns Fontanas grafiska manual med riktlinjer för varumärket.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Perfekt referens för logotyper, färger och typsnitt!
          </Typography>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      id: 'complete',
      target: 'body',
      title: '🎉 Klart!',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Nu kan du börja använda DAM-systemet! 
            Klicka på <strong>?</strong>-knappen längst ner om du behöver hjälp.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Lycka till! 🚀
          </Typography>
        </Box>
      ),
      placement: 'center'
    }
  ];

  // Extra steg för byrå-användare (kan ladda upp)
  const byraSteps: TourStep[] = [
    {
      id: 'upload',
      target: '.upload-button',
      title: '📤 Ladda upp bilder',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Klicka här för att ladda upp nya bilder till systemet.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Välj mall och fyll i metadata för bättre sökbarhet.
          </Typography>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      id: 'upload-queue',
      target: '.upload-button',
      title: '⏳ Uppladdning: Kö vs Direkt',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
            Det finns två sätt att ladda upp:
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            <strong>• Direkt uppladdning:</strong> Filer laddas upp omedelbart med AI-analys.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            <strong>• Köad uppladdning:</strong> Samla flera filer i kön och ladda upp i bakgrunden.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Köad uppladdning är bra för många filer – du kan fortsätta arbeta!
          </Typography>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      id: 'asset-icons',
      target: '.asset-grid',
      title: '🔖 Ikoner på bildkort',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
            Varje bildkort visar status med ikoner:
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
            <strong>✓</strong> (bock) = Markerad/vald<br/>
            <strong>Badges</strong> = Färgade etiketter (t.ex. "Ny", "Kampanj")<br/>
            <strong>AI: pending</strong> = AI-analys pågår
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Klicka på ⋮ (tre prickar) för att se fler åtgärder!
          </Typography>
        </Box>
      ),
      placement: 'top'
    },
    {
      id: 'share-collection',
      target: '.collection-sidebar',
      title: '🔗 Dela samlingar',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Klicka på <strong>⋮ (tre prickar)</strong> bredvid en samling och välj <strong>"Förhandsvisa delad sida"</strong> för att se hur sidan kommer att se ut när den delas.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Välj <strong>"Inställningar"</strong> om du vill göra ändringar i den delade samlingen. Samlingar kan vara privata eller allmänna. Privata samlingar ser bara du. Samlingarna syns även i PowerPoint-pluginen, så du kan skapa egna privata samlingar med utvalda bilder.
          </Typography>
        </Box>
      ),
      placement: 'right'
    },
    {
      id: 'metadata',
      target: '.asset-detail-panel',
      title: '📝 Metadata & Detaljer',
      content: (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              När du klickar på en bild öppnas denna panel med all information.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
              Redigera metadata, ladda ner i olika format, och hantera versioner.
            </Typography>
          </Box>
          <Box
            component="img"
            src="/help-asset-view.jpg"
            alt="Exempel på asset view"
            sx={{
              width: 140,
              height: 'auto',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              flexShrink: 0
            }}
          />
        </Box>
      ),
      placement: 'left'
    }
  ];

  // Extra steg för admin
  const adminSteps: TourStep[] = [
    {
      id: 'settings',
      target: '.settings-button',
      title: '⚙️ Inställningar',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Som admin har du tillgång till systeminställningar:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.5, fontSize: '0.9rem' }}>
            • Användare & Roller<br/>
            • Kategorier & Mallar<br/>
            • Badges & Backup<br/>
            • CSV-import/export
          </Typography>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      id: 'templates-admin',
      target: '.settings-button',
      title: '📋 Mallar (Templates)',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Mallar bestämmer vilka metadatafält som visas för olika bildtyper.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.5, fontSize: '0.9rem' }}>
            <strong>Exempel:</strong><br/>
            • <strong>Produktbilder:</strong> GTIN, artikelnr, Validoo-data<br/>
            • <strong>Vin:</strong> Druva, årgång, region, producent
          </Typography>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      id: 'ai-queue',
      target: 'body',
      title: '🤖 AI-kö',
      content: (
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Uppladdade bilder analyseras automatiskt av AI för taggar och beskrivningar.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Du kan se AI-köstatus under Inställningar → AI-kö.
          </Typography>
        </Box>
      ),
      placement: 'center'
    }
  ];

  // Bygg steg baserat på roll
  let steps = [...baseSteps];
  
  if (role === 'byra') {
    // Byrå: grundsteg + uppladdning/redigering
    steps = [...baseSteps.slice(0, -1), ...byraSteps, baseSteps[baseSteps.length - 1]];
  } else if (role === 'admin') {
    // Admin: allt
    steps = [...baseSteps.slice(0, -1), ...byraSteps, ...adminSteps, baseSteps[baseSteps.length - 1]];
  }
  // Kontor: bara baseSteps (ingen uppladdning)

  return steps;
};

const GuidedTour: React.FC<GuidedTourProps> = ({
  run,
  onTourEnd,
  onStepChange,
  userRole = 'kontor'
}) => {
  // useMemo så att stegen uppdateras när userRole ändras
  const steps = React.useMemo(() => {
    console.log('[GuidedTour] Creating steps for role:', userRole);
    const result = createSteps(userRole);
    console.log('[GuidedTour] Total steps:', result.length, result.map(s => s.id));
    return result;
  }, [userRole]);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);

  // Beräkna position för tooltip baserat på target-element
  const updatePosition = useCallback(() => {
    if (!run || currentStep >= steps.length) return;

    const step = steps[currentStep];
    
    if (step.placement === 'center' || step.target === 'body') {
      // Centrera tooltip
      setTooltipPosition({
        top: window.innerHeight / 2 - 150,
        left: window.innerWidth / 2 - 200
      });
      setSpotlightRect(null);
      return;
    }

    // Hitta target-elementet
    const element = document.querySelector(step.target);
    if (!element) {
      // Om element inte hittas, centrera
      setTooltipPosition({
        top: window.innerHeight / 2 - 150,
        left: window.innerWidth / 2 - 200
      });
      setSpotlightRect(null);
      return;
    }

    const rect = element.getBoundingClientRect();
    setSpotlightRect(rect);

    // Beräkna tooltip-position baserat på placement
    const tooltipWidth = 400;
    const tooltipHeight = 200;
    const padding = 16;

    let top = 0;
    let left = 0;

    switch (step.placement) {
      case 'top':
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding;
        break;
      default:
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    // Säkerställ att tooltip är synlig på skärmen
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

    setTooltipPosition({ top, left });
  }, [run, currentStep, steps]);

  // Uppdatera position när steg ändras eller fönstret ändras storlek
  useEffect(() => {
    if (!run) return;

    updatePosition();
    
    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [run, currentStep, updatePosition]);

  // Reset när tour startar
  useEffect(() => {
    if (run) {
      setCurrentStep(0);
    }
  }, [run]);

  // Notifiera om stegbyte
  useEffect(() => {
    if (run && onStepChange && currentStep < steps.length) {
      onStepChange(currentStep, steps[currentStep].id);
    }
  }, [run, currentStep, steps, onStepChange]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onTourEnd();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onTourEnd();
  };

  if (!run) return null;

  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Beräkna padding runt spotlight
  const spotlightPadding = 8;

  return (
    <>
      {/* Backdrop med rektangulär spotlight */}
      {spotlightRect ? (
        // Med target: skapa spotlight genom att rita 4 mörka rektanglar runt target
        <>
          {/* Top overlay */}
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: Math.max(0, spotlightRect.top - spotlightPadding),
            backgroundColor: 'rgba(27, 57, 103, 0.75)',
            zIndex: 9998,
            pointerEvents: 'none'
          }} />
          {/* Bottom overlay */}
          <Box sx={{
            position: 'fixed',
            top: spotlightRect.bottom + spotlightPadding,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(27, 57, 103, 0.75)',
            zIndex: 9998,
            pointerEvents: 'none'
          }} />
          {/* Left overlay */}
          <Box sx={{
            position: 'fixed',
            top: spotlightRect.top - spotlightPadding,
            left: 0,
            width: Math.max(0, spotlightRect.left - spotlightPadding),
            height: spotlightRect.height + spotlightPadding * 2,
            backgroundColor: 'rgba(27, 57, 103, 0.75)',
            zIndex: 9998,
            pointerEvents: 'none'
          }} />
          {/* Right overlay */}
          <Box sx={{
            position: 'fixed',
            top: spotlightRect.top - spotlightPadding,
            left: spotlightRect.right + spotlightPadding,
            right: 0,
            height: spotlightRect.height + spotlightPadding * 2,
            backgroundColor: 'rgba(27, 57, 103, 0.75)',
            zIndex: 9998,
            pointerEvents: 'none'
          }} />
          {/* Spotlight border (subtil) */}
          <Box sx={{
            position: 'fixed',
            top: spotlightRect.top - spotlightPadding,
            left: spotlightRect.left - spotlightPadding,
            width: spotlightRect.width + spotlightPadding * 2,
            height: spotlightRect.height + spotlightPadding * 2,
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 1,
            zIndex: 9999,
            pointerEvents: 'none',
            boxShadow: '0 0 0 9999px rgba(27, 57, 103, 0)'
          }} />
        </>
      ) : (
        // Utan target: full backdrop
        <Backdrop
          open={run}
          sx={{
            zIndex: 9998,
            backgroundColor: 'rgba(27, 57, 103, 0.75)'
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Tooltip */}
      <Fade in={run}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            width: 400,
            maxWidth: 'calc(100vw - 32px)',
            zIndex: 10000,
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          {/* Progress bar */}
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{
              height: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#1B3967'
              }
            }}
          />

          {/* Header */}
          <Box sx={{ 
            p: 2, 
            pb: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderBottom: '1px solid #eee'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1B3967', fontSize: '1.1rem' }}>
              {step.title}
            </Typography>
            <IconButton size="small" onClick={handleSkip} sx={{ color: '#999' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2, pt: 1.5 }}>
            {step.content}
          </Box>

          {/* Footer */}
          <Box sx={{ 
            p: 2, 
            pt: 0,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: 1
          }}>
            <Typography variant="caption" sx={{ color: '#999' }}>
              {currentStep + 1} / {steps.length}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isFirstStep && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handlePrev}
                  startIcon={<ArrowBackIcon />}
                  sx={{ color: '#666' }}
                >
                  Tillbaka
                </Button>
              )}
              
              {isFirstStep && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSkip}
                  sx={{ color: '#999' }}
                >
                  Hoppa över
                </Button>
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleNext}
                endIcon={!isLastStep ? <ArrowForwardIcon /> : undefined}
                sx={{
                  bgcolor: '#1B3967',
                  '&:hover': { bgcolor: '#0D1F3C' },
                  fontWeight: 600,
                  px: 2
                }}
              >
                {isLastStep ? 'Slutför' : 'Nästa'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default GuidedTour;
